import React, { useState, useEffect, useRef } from 'react';
import { searchWPPosts } from '../services/posts';
import { RESTPost } from '../services/types';
import BDPost from '../components/BDPost';
import AdSense from '../components/AdSense';
import Header from '../components/Header';
import Button from '../components/Button';
import { XCircleIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import './SearchPage.css';

const SEARCH_HISTORY_KEY = 'bd_search_history';
const MAX_HISTORY_ITEMS = 10;

const SearchPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchHistory, setSearchHistory] = useState<string[]>([]);
    const [searchResults, setSearchResults] = useState<RESTPost[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [lastSearchTerm, setLastSearchTerm] = useState('');
    const [searchTranscripts, setSearchTranscripts] = useState(false);
    const [lastSearchTranscripts, setLastSearchTranscripts] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Load search history from localStorage
    useEffect(() => {
        const history = localStorage.getItem(SEARCH_HISTORY_KEY);
        if (history) {
            try {
                setSearchHistory(JSON.parse(history));
            } catch (e) {
                console.error('Failed to parse search history', e);
            }
        }
    }, []);

    // Save search history to localStorage
    const saveSearchHistory = (term: string) => {
        const updatedHistory = [term, ...searchHistory.filter(h => h !== term)].slice(0, MAX_HISTORY_ITEMS);
        setSearchHistory(updatedHistory);
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    };

    // Remove item from search history
    const removeFromHistory = (term: string) => {
        const updatedHistory = searchHistory.filter(h => h !== term);
        setSearchHistory(updatedHistory);
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    };

    // Perform search
    const performSearch = async (term: string, page: number = 1, transcripts: boolean = searchTranscripts) => {
        if (term.length < 3) return;

        setIsLoading(true);
        setIsSearchActive(true);
        setLastSearchTerm(term);
        setLastSearchTranscripts(transcripts);

        try {
            const response = await searchWPPosts(term, page, transcripts);

            if (page === 1) {
                setSearchResults(response.posts);
                saveSearchHistory(term);
            } else {
                setSearchResults(prev => [...prev, ...response.posts]);
            }
            
            setCurrentPage(page);
            setTotalPages(response.totalPages);
        } catch (error) {
            console.error('Search failed', error);
            if (page === 1) {
                setSearchResults([]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search button click
    const handleSearch = () => {
        if (searchTerm.length >= 3) {
            performSearch(searchTerm, 1);
        }
    };

    // Handle history item click
    const handleHistoryClick = (term: string) => {
        setSearchTerm(term);
        performSearch(term, 1);
    };

    // Handle clear input
    const handleClearInput = () => {
        setSearchTerm('');
        inputRef.current?.focus();
    };

    // Handle load more
    const handleLoadMore = () => {
        if (currentPage < totalPages && !isLoading) {
            performSearch(lastSearchTerm, currentPage + 1, lastSearchTranscripts);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchTerm.length >= 3 && !isLoading) {
            handleSearch();
        }
    };

    const isSearchButtonEnabled = searchTerm.length >= 3 && !isLoading;

    return (
        <div className="Page searchPage postsList">
            <Header />
            
            <div className="search-input-wrapper">
                <div className="search-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        className="search-input"
                        placeholder="Rechercher des BDs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                    
                    {searchTerm.length > 0 && (
                        <button
                            className="clear-input-button"
                            onClick={handleClearInput}
                            disabled={isLoading}
                            aria-label="Effacer"
                        >
                            <XCircleIcon className="icon" />
                        </button>
                    )}
                </div>

                <label className="search-transcripts-toggle">
                    <input
                        type="checkbox"
                        checked={searchTranscripts}
                        onChange={(e) => setSearchTranscripts(e.target.checked)}
                        disabled={isLoading}
                    />
                    <span>Rechercher aussi dans le texte des bulles</span>
                </label>

                <button
                    className={`search-button ${isSearchButtonEnabled ? 'enabled' : 'disabled'}`}
                    onClick={handleSearch}
                    disabled={!isSearchButtonEnabled}
                >
                    Rechercher
                </button>
            </div>

            {!isSearchActive && searchHistory.length > 0 && (
                <div className="search-history">
                    <h3 className="search-history-title">Recherches récentes</h3>
                    <ul className="search-history-list">
                        {searchHistory.map((term, index) => (
                            <li key={index} className="search-history-item">
                                <button
                                    className="search-history-term"
                                    onClick={() => handleHistoryClick(term)}
                                >
                                    <ClockIcon className="history-icon" />
                                    {term}
                                </button>
                                <button
                                    className="remove-history-button"
                                    onClick={() => removeFromHistory(term)}
                                    aria-label="Supprimer"
                                >
                                    <XCircleIcon className="icon" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {isLoading && currentPage === 1 && (
                <div className="search-loading">
                    <ArrowPathIcon className="loading-spinner" />
                </div>
            )}

            {isSearchActive && !isLoading && searchResults.length === 0 && (
                <div className="no-results">
                    <MagnifyingGlassIcon className="no-results-icon" />
                    <p>Aucun résultat</p>
                </div>
            )}

            {searchResults.map((post, index) => (
                <React.Fragment key={post.id}>
                    <BDPost post={post} />
                    {/* Insert ad after 1st, 4th, and 6th posts */}
                    {(index === 0 || index === 3 || index === 5) && (
                        <AdSense />
                    )}
                </React.Fragment>
            ))}
            
            {searchResults.length > 0 && currentPage < totalPages && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Button 
                        variant="secondary" 
                        size="medium" 
                        onClick={handleLoadMore}
                        loading={isLoading}
                    >
                        {isLoading ? 'Chargement...' : 'Charger plus de BDs'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SearchPage;

